// Shared body-scroll lock so multiple modals can stack safely. Each caller
// increments a ref count on lock and releases on unmount; we only touch
// document styles at the 0↔1 boundary. Uses position:fixed on `body` so the
// page behind overlays cannot scroll (including mobile touch scroll).

let locks = 0
let savedScrollY = 0

type SavedStyles = {
  bodyOverflow: string
  bodyPosition: string
  bodyTop: string
  bodyWidth: string
  bodyPaddingRight: string
  htmlOverflow: string
}

let saved: SavedStyles | null = null

function scrollbarWidth(): number {
  return window.innerWidth - document.documentElement.clientWidth
}

export function lockBodyScroll(): () => void {
  if (typeof document === 'undefined') return () => {}

  if (locks === 0) {
    savedScrollY = window.scrollY
    const pad = scrollbarWidth()

    saved = {
      bodyOverflow: document.body.style.overflow,
      bodyPosition: document.body.style.position,
      bodyTop: document.body.style.top,
      bodyWidth: document.body.style.width,
      bodyPaddingRight: document.body.style.paddingRight,
      htmlOverflow: document.documentElement.style.overflow,
    }

    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollY}px`
    document.body.style.width = '100%'
    if (pad > 0) {
      document.body.style.paddingRight = `${pad}px`
    }
  }

  locks += 1

  let released = false
  return () => {
    if (released) return
    released = true
    locks = Math.max(0, locks - 1)
    if (locks !== 0 || !saved) return

    const styles = saved
    saved = null

    document.documentElement.style.overflow = styles.htmlOverflow
    document.body.style.overflow = styles.bodyOverflow
    document.body.style.position = styles.bodyPosition
    document.body.style.top = styles.bodyTop
    document.body.style.width = styles.bodyWidth
    document.body.style.paddingRight = styles.bodyPaddingRight
    window.scrollTo(0, savedScrollY)
  }
}
